/**
 * Utility functions for date/time formatting with proper timezone handling
 */

/**
 * Normalizes a date string to ensure proper UTC parsing.
 * If the date string doesn't have timezone info, assumes UTC.
 * 
 * @param dateString - ISO date string (may or may not have timezone info)
 * @returns Date object parsed as UTC
 */
export function parseUTCDate(dateString: string): Date {
  if (!dateString) {
    return new Date();
  }
  
  // If already has timezone info (Z, +, or - after position 10), parse normally
  if (dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
    return new Date(dateString);
  }
  
  // No timezone info - assume UTC and append 'Z'
  return new Date(dateString + 'Z');
}

/**
 * Formats a date string to local time string (HH:MM format)
 * 
 * @param dateString - ISO date string
 * @returns Formatted time string in local timezone
 */
export function formatTime(dateString: string): string {
  const date = parseUTCDate(dateString);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats a date string to local date string
 * 
 * @param dateString - ISO date string
 * @param options - Optional formatting options
 * @returns Formatted date string in local timezone
 */
export function formatDate(
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string {
  const date = parseUTCDate(dateString);
  return date.toLocaleDateString('en-US', options);
}

